/* eslint-disable jsx-a11y/no-redundant-roles -- role='list' / 'listitem' are the design's: Safari drops list semantics from a list styled with list-style: none */
import { Trans, useTranslation } from 'next-i18next';
import type { ReactElement } from 'react';
import { memo, useEffect, useRef } from 'react';

import { TrialCta } from '../components/trial-cta';
import { CONTAINER_CLASS, EYEBROW_CLASS, KICKER_CLASS, SECTION_CLASS, SectionBackground } from '../components/ui';
import { FINAL_FORM_ANCHOR, V4A_NAMESPACE } from '../constants';
import { loadGeoData, localeTag } from '../engines/geo';
import { startMultiInternet } from '../engines/multi-internet-engine';

type SlotId = 'fibra' | 'wifi' | 'g5' | 'cabo' | 'isp2' | 'g4';

interface StaticConnection {
  id: SlotId;
  mbps: number;
  on: boolean;
  icon: ReactElement;
}

/* The source's first paint: two connections active (Fibra + Wi-Fi 6), four in reserve. The engine then
   renames every row after the providers of the visitor's country. */
const CONNECTIONS: readonly StaticConnection[] = [
  {
    id: 'fibra',
    mbps: 500,
    on: true,
    icon: (
      <svg viewBox='0 0 24 24'>
        <path d='M3 18c4.75 0 5-11.5 9.75-11.5H14' />
        <circle cx='16' cy='6.5' r='2' />
        <path d='M19 3.5l1.25-1.25M19.75 6.5H21.5M19 9.5l1.25 1.25' />
      </svg>
    ),
  },
  {
    id: 'wifi',
    mbps: 300,
    on: true,
    icon: (
      <svg viewBox='0 0 24 24'>
        <path d='M3 9.75a13 13 0 0 1 18 0M6.25 13a8.5 8.5 0 0 1 11.5 0M9.5 16.25a4.25 4.25 0 0 1 5 0' />
        <circle cx='12' cy='19.75' r='.9' />
      </svg>
    ),
  },
  {
    id: 'g5',
    mbps: 150,
    on: false,
    icon: (
      <svg viewBox='0 0 24 24'>
        <rect x='6.5' y='2.75' width='11' height='18.5' rx='2.25' />
        <path d='M10.5 18.25h3M8.75 12.25a4.75 4.75 0 0 1 6.5 0' />
        <circle cx='12' cy='14.75' r='.9' />
      </svg>
    ),
  },
  {
    id: 'cabo',
    mbps: 250,
    on: false,
    icon: (
      <svg viewBox='0 0 24 24'>
        <path d='M5 3h14v9.5h-3.5v3h-7v-3H5z' />
        <path d='M8.5 3v3M12 3v3M15.5 3v3' />
        <path d='M12 15.5v5.5' />
      </svg>
    ),
  },
  {
    id: 'isp2',
    mbps: 200,
    on: false,
    icon: (
      <svg viewBox='0 0 24 24'>
        <rect x='3' y='13' width='18' height='7.5' rx='2' />
        <path d='M6.5 16.75h.01M9.75 16.75h.01M16.5 13V6.25M13.25 6a4.5 4.5 0 0 1 6.5 0' />
      </svg>
    ),
  },
  {
    id: 'g4',
    mbps: 60,
    on: false,
    icon: (
      <svg viewBox='0 0 24 24'>
        <rect x='3' y='14.5' width='3' height='6.5' rx='.9' />
        <rect x='8.2' y='10.75' width='3' height='10.25' rx='.9' />
        <rect x='13.4' y='7' width='3' height='14' rx='.9' />
        <rect x='18.6' y='3' width='3' height='18' rx='.9' />
      </svg>
    ),
  },
];

function ConnectionRow({ connection }: { connection: StaticConnection }) {
  const { t } = useTranslation(V4A_NAMESPACE);
  const name = t(`mi.static.${connection.id}.name`);
  const label = connection.on ? t('mi.label.on', { name, mbps: connection.mbps }) : t('mi.label.reserve', { name, mbps: connection.mbps });
  return (
    <li role='listitem'>
      <button className={`mi-route ${connection.on ? 'is-on' : 'is-off'}`} type='button' data-conn={connection.id} aria-pressed={connection.on} aria-label={label}>
        <span className='mi-route-cc' aria-hidden='true'>
          {connection.icon}
        </span>
        <span className='mi-route-body'>
          <span className='mi-route-city'>{t(`mi.static.${connection.id}.title`)}</span>
          <span className='mi-route-meta'>
            <span className='mi-route-kind'>{t(`mi.static.${connection.id}.kind`)}</span>
            <span className='mi-route-tag' data-tag=''>
              {connection.on ? t('mi.tag.on') : t('mi.tag.reserve')}
            </span>
          </span>
        </span>
        <svg className='mi-route-spark' viewBox='0 0 52 18' aria-hidden='true' focusable='false'>
          <polygon className='sp-fill' points='' />
          <polyline points='' />
          <circle className='sp-dot' r='1.75' />
        </svg>
        <span className='mi-route-lat'>
          <span className='mi-route-ms'>
            <span data-mb=''>{connection.mbps}</span>
            <span className='u'>Mb/s</span>
          </span>
          <span className='mi-route-delta' data-delta=''>
            {connection.on ? t('mi.delta.on') : t('mi.delta.reserve')}
          </span>
        </span>
        <span className='mi-route-tg' aria-hidden='true'>
          <svg viewBox='0 0 24 24'>
            <path className='t-off' d='M12 5.5v13M5.5 12h13' />
            <path className='t-on' d='M5 12.5l4.5 4.5L19 7' />
            <path className='t-down' d='M19 12a7 7 0 1 1-2.05-4.95M19 4.5v3.5h-3.5' />
          </svg>
        </span>
      </button>
    </li>
  );
}

const NODE_PC_ICON = (
  <svg viewBox='0 0 40 40'>
    <rect x='5' y='7' width='30' height='20' rx='3' />
    <path d='M14 34h12M20 27v7' />
    <circle cx='20' cy='17' r='5' />
    <path d='M20 9v3.5M20 21.5V25M12 17h3.5M24.5 17H28' />
    <circle className='f' cx='20' cy='17' r='1.4' />
  </svg>
);

function MultiInternetCardMarkup() {
  const { t } = useTranslation(V4A_NAMESPACE);
  const initialSum = `${t('mi.static.fibra.title')} + <span class="nb">${t('mi.static.wifi.title')}</span>`;
  return (
    <section className='mi' aria-labelledby='mi-title' data-mi=''>
      <div className='mi-card'>
        <header className='mi-head'>
          <span className='eyebrow'>Multi Internet</span>
          <h2 className='mi-title' id='mi-title'>
            <Trans t={t} i18nKey='mi.title' components={{ a: <span className='mi-title-a' />, b: <span className='mi-title-b' />, em: <em /> }} />
          </h2>
          <p className='mi-lead'>
            <span className='x-desk'>
              <Trans t={t} i18nKey='mi.lead_desktop' components={{ b: <b /> }} />
            </span>
            <span className='x-mobile'>
              <Trans t={t} i18nKey='mi.lead_mobile' components={{ b: <b /> }} />
            </span>
          </p>
          <ul className='mi-facts' role='list'>
            <li>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M12 3.5 5 6.25v5.5c0 4.4 3 7.6 7 8.75 4-1.15 7-4.35 7-8.75v-5.5z' />
                <path d='M12.75 8.5 10 12.5h4l-2.75 4' />
              </svg>
              <span>
                <Trans t={t} i18nKey='mi.fact_failover' components={{ b: <b /> }} />
              </span>
            </li>
            <li>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <circle cx='9.25' cy='8.25' r='3.25' />
                <path d='M2.75 19.5c0-3.15 2.9-5.25 6.5-5.25s6.5 2.1 6.5 5.25' />
                <path d='M16.25 5.4a3.05 3.05 0 0 1 0 5.7M17.9 14.6c2.15.62 3.35 2.4 3.35 4.9' />
              </svg>
              <span>
                <Trans t={t} i18nKey='mi.fact_players' components={{ b: <b /> }} />
              </span>
            </li>
          </ul>
        </header>

        <div className='mi-proof' role='group' aria-label={t('mi.proof_label')} data-proof=''>
          <p className='sr-only' data-proof-sr=''>
            {t('mi.proof_sr', { base: t('mi.cap.mbps_sr', { value: 500 }), sum: t('mi.cap.mbps_sr', { value: 800 }), gain: t('mi.proof_sr_gain', { pct: 60 }), drops: t('mi.drops.none') })}
          </p>
          <div className='mi-proof-rows' aria-hidden='true'>
            <div className='mi-proof-item mi-proof-item--bad'>
              <span className='mi-proof-line'>
                <small>{t('mi.proof_single')}</small>
                <span className='mi-proof-sp' />
                <strong>
                  <span data-p-base=''>500</span>
                  <span className='u' data-p-base-u=''>
                    Mb/s
                  </span>
                </strong>
              </span>
              <span className='mi-proof-bar'>
                <span className='mi-proof-fill' data-p-fill='' />
              </span>
            </div>
            <div className='mi-proof-item mi-proof-item--good'>
              <span className='mi-proof-line'>
                <small>{t('mi.proof_combined')}</small>
                <span className='mi-proof-sp' />
                <span className='mi-proof-pct' data-p-pct=''>
                  +60%
                </span>
                <strong>
                  <span data-p-sum=''>800</span>
                  <span className='u' data-p-sum-u=''>
                    Mb/s
                  </span>
                </strong>
              </span>
              <span className='mi-proof-bar'>
                <span className='mi-proof-fill' data-p-fill-good='' />
              </span>
            </div>
          </div>
          <p className='mi-proof-note' aria-hidden='true'>
            <span className='mi-proof-tag' data-p-drops=''>
              {t('mi.drops.none')}
            </span>
            <Trans t={t} i18nKey='mi.proof_note' components={{ b: <b /> }} />
          </p>
        </div>

        <p className='sr-only' id='mi-desc' data-mi-desc=''>
          {t('mi.description', { connections: CONNECTIONS.map((connection) => t(`mi.static.${connection.id}.name`)).join(', ') })}
        </p>
        <div className='mi-stage' data-stage='' role='group' aria-label={t('mi.stage_label')} aria-describedby='mi-desc'>
          <p className='sr-only' data-sr-now=''>
            {t('mi.sr_now.initial')}
          </p>
          <svg className='mi-wires' data-wires='' aria-hidden='true' focusable='false' />
          <div className='mi-packets' data-packets='' aria-hidden='true' />
          <div className='mi-nojs-rail' aria-hidden='true' />
          <div className='mi-node mi-node--pc' data-node='pc'>
            <div className='mi-node-icon' aria-hidden='true'>
              {NODE_PC_ICON}
            </div>
            <div className='mi-node-label'>
              <strong>{t('mi.pc')}</strong>
              <small>{t('mi.pc_place')}</small>
            </div>
          </div>
          <div className='mi-routes-wrap'>
            <div className='mi-key'>
              <span className='mi-key-col' aria-hidden='true' data-key=''>
                <b>
                  <Trans t={t} i18nKey='mi.key' values={{ total: 6 }} components={{ count: <span data-count='' />, word: <span data-count-w='' /> }} />
                </b>
              </span>
              <p className='mi-hint' data-hint='' aria-hidden='true'>
                <svg className='x-touch' viewBox='0 0 24 24'>
                  <circle className='dot' cx='15' cy='15' r='2.4' />
                  <path d='M9.17 17.12A6.2 6.2 0 0 1 17.12 9.17M5.04 18.63A10.6 10.6 0 0 1 18.63 5.04' />
                </svg>
                <svg className='x-hover' viewBox='0 0 24 24'>
                  <path d='M5 3l14 8.2-6.4 1.6L9.4 19z' />
                </svg>
                <svg className='x-after' viewBox='0 0 24 24'>
                  <path d='M4.5 12.5l4.5 4.5L19.5 6.5' />
                </svg>
                <span className='x-touch'>{t('mi.hint_touch')}</span>
                <span className='x-hover'>{t('mi.hint_hover')}</span>
                <span className='x-after'>{t('mi.hint_after')}</span>
              </p>
            </div>
            <svg className='mi-defs' aria-hidden='true' focusable='false'>
              <defs>
                <linearGradient id='mi-spg' x1='0' y1='0' x2='0' y2='1'>
                  <stop className='g0' offset='0' />
                  <stop className='g1' offset='1' />
                </linearGradient>
              </defs>
            </svg>
            <ul className='mi-routes' role='list' data-routes='' aria-label={t('mi.routes_label')}>
              {CONNECTIONS.map((connection) => (
                <ConnectionRow key={connection.id} connection={connection} />
              ))}
            </ul>
          </div>
          <div className='mi-node mi-node--np' data-node='np'>
            <div className='mi-node-icon' aria-hidden='true'>
              <svg viewBox='0 0 40 40'>
                <path d='M5 11h6c6 0 7 9 13 9' />
                <path d='M5 29h6c6 0 7-9 13-9' />
                <path d='M5 20h19' />
                <path d='M24 20h10.5M30.5 16.5 34.5 20l-4 3.5' />
                <circle className='f' cx='24' cy='20' r='2.3' />
              </svg>
              <span className='mi-burst' />
            </div>
            <div className='mi-node-label'>
              <strong>NoPing</strong>
              <small className='mi-node-sub'>
                <span data-np-sub=''>{t('mi.np_sub.combines')}</span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.np_sub.took_over')}
                </span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.np_sub.combines')}
                </span>
              </small>
            </div>
          </div>
          <div className='mi-node mi-node--game' data-node='game'>
            <div className='mi-node-icon' aria-hidden='true'>
              <svg viewBox='0 0 40 40'>
                <rect x='7' y='7' width='26' height='10' rx='2.5' />
                <rect x='7' y='23' width='26' height='10' rx='2.5' />
                <path d='M12 12h6M12 28h6' />
                <circle className='f' cx='27' cy='12' r='1.6' />
                <circle className='f' cx='27' cy='28' r='1.6' />
              </svg>
            </div>
            <div className='mi-node-label'>
              <strong>{t('mi.game')}</strong>
              <small className='mi-node-sub'>
                <span data-game-sub=''>{t('mi.game_sub.continuous')}</span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.game_sub.continuous')}
                </span>
              </small>
            </div>
          </div>
          <div className='mi-backup' data-backup='' aria-hidden='true'>
            <p className='mi-direct-key'>{t('mi.backup_key')}</p>
            <div className='mi-backup-row'>
              <svg className='mi-backup-fan' viewBox='0 0 44 22' aria-hidden='true' focusable='false'>
                <path className='bf-l' d='M22 3 2 19M22 3 6.4 19M22 3 10.9 19M22 3 15.3 19M22 3 19.8 19M22 3 24.2 19M22 3 28.7 19M22 3 33.1 19M22 3 37.6 19M22 3 42 19' />
                <path className='bf-d' d='M2 19v0M6.4 19v0M10.9 19v0M15.3 19v0M19.8 19v0M24.2 19v0M28.7 19v0M33.1 19v0M37.6 19v0M42 19v0' />
              </svg>
              <b data-backup-b=''>{t('mi.backup.rest')}</b>
              <strong>10</strong>
              <small className='mi-backup-sub'>
                <span data-backup-sub=''>{t('mi.backup.rest_sub')}</span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.backup.rest_sub')}
                </span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.backup.hot_sub')}
                </span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.backup.lone_sub')}
                </span>
                <span className='mi-node-ghost' aria-hidden='true'>
                  {t('mi.backup.none_sub')}
                </span>
              </small>
            </div>
          </div>
          <div className='mi-result' aria-hidden='true'>
            <p className='mi-direct-key'>{t('mi.result_key')}</p>
            <div className='mi-direct' data-sum-row=''>
              <i />
              <span className='mi-direct-txt'>
                <b data-sum-b=''>{t('mi.sum.continuous')}</b>
                <span className='x-sep' />
                <span className='mi-direct-sub' data-sum-sub='' dangerouslySetInnerHTML={{ __html: `<span class="x-long">${t('mi.sum_sub.pair', { names: initialSum, interpolation: { escapeValue: false } })}</span><span class="x-short">${t('mi.sum_sub.pair_short', { names: initialSum, interpolation: { escapeValue: false } })}</span>` }} />
              </span>
              <strong>
                <span data-sum=''>800</span>
                <span className='u' data-sum-u=''>
                  Mb/s
                </span>
              </strong>
            </div>
          </div>
        </div>

        <footer className='mi-foot'>
          <div className='mi-foot-text'>
            <p className='mi-status' data-status=''>
              <i aria-hidden='true' />
              <span className='mi-status-body' data-status-text='' dangerouslySetInnerHTML={{ __html: `<b class="mi-stab">${t('mi.stab.ok')}</b> · ${t('mi.status_initial', { interpolation: { escapeValue: false } })}` }} />
            </p>
            <div className='mi-foot-slot'>
              <p className='mi-bridge' data-bridge=''>
                {t('mi.bridge')}
              </p>
            </div>
          </div>
          <div className='mi-actions'>
            <button className='mi-pause' type='button' data-pause='' aria-label={t('mi.pause_label')} aria-pressed='false' title={t('mi.pause_title')}>
              <svg className='ic-pause' viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M8.5 5.5v13M15.5 5.5v13' />
              </svg>
              <svg className='ic-play' viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M8 5.2v13.6a.8.8 0 0 0 1.2.7l11-6.8a.8.8 0 0 0 0-1.4l-11-6.8a.8.8 0 0 0-1.2.7z' />
              </svg>
            </button>
            <button className='btn btn-secondary mi-btn' type='button' data-drop='' aria-busy='false' title={t('mi.drop_title.drop')}>
              <svg viewBox='0 0 24 24' aria-hidden='true'>
                <path d='M13.5 3 6 13h5l-1 8L18 11h-5z' />
                <path d='M4 4l16 16' />
              </svg>
              <span className='mi-btn-lbl'>
                <span data-lbl=''>{t('mi.drop')}</span>
                <span className='mi-btn-ghost' aria-hidden='true'>
                  {t('mi.drop')}
                </span>
                <span className='mi-btn-ghost' aria-hidden='true'>
                  {t('mi.reconnecting')}
                </span>
                <span className='mi-btn-ghost' aria-hidden='true'>
                  {t('mi.reconnect')}
                </span>
              </span>
            </button>
            <div className='mi-offer'>
              <TrialCta className='btn btn-primary mi-cta' anchor={FINAL_FORM_ANCHOR} data-cta=''>
                <span>{t('cta.try_free')}</span>
                <svg viewBox='0 0 24 24' aria-hidden='true'>
                  <path d='M5 12h13M13 6l6 6-6 6' />
                </svg>
              </TrialCta>
              <p className='mi-offer-note'>
                <Trans t={t} i18nKey='mi.offer_note' components={{ b: <b /> }} />
              </p>
            </div>
          </div>
          <p className='sr-only' role='status' aria-live='polite' data-live='' />
        </footer>
      </div>
    </section>
  );
}

/** Once the engine owns the live nodes, React never renders this subtree again. */
const MultiInternetCard = memo(MultiInternetCardMarkup);

/** "Aula II · Feitiço de proteção": the Multi Internet card, native (it was an iframe in the source). */
export function MultiInternet() {
  const { t, i18n } = useTranslation(V4A_NAMESPACE);
  const holdRef = useRef<HTMLDivElement>(null);
  const locale = i18n.language;

  useEffect(() => {
    const root = holdRef.current?.querySelector<HTMLElement>('[data-mi]');
    if (!root) return undefined;
    let stop: (() => void) | null = null;
    let cancelled = false;
    const tag = localeTag(locale);
    const listFormat = typeof Intl.ListFormat === 'function' ? new Intl.ListFormat(tag, { style: 'long', type: 'conjunction' }) : null;
    loadGeoData()
      .then((geo) => {
        if (cancelled) return;
        stop = startMultiInternet(root, {
          geo,
          translate: (key, values) => String(i18n.t(key, { ns: V4A_NAMESPACE, ...values })),
          joinList: (items) => (listFormat ? listFormat.format(items) : items.join(', ')),
          formatNumber: (value, fractionDigits) => new Intl.NumberFormat(tag, { minimumFractionDigits: 0, maximumFractionDigits: fractionDigits }).format(value),
        });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
      stop?.();
    };
  }, [i18n, locale]);

  return (
    <section className={`${SECTION_CLASS} sec--frame`} id='patrono' aria-labelledby='mi-kicker'>
      <SectionBackground halos={[{ position: 'right', tone: 'violet' }]} />
      <div className={CONTAINER_CLASS}>
        <p className={EYEBROW_CLASS} data-reveal=''>
          {t('mi.section_eyebrow')}
        </p>
        <h2 className={KICKER_CLASS} id='mi-kicker' data-reveal=''>
          <Trans t={t} i18nKey='mi.kicker' components={{ b: <b className='font-bold text-[color:var(--fg-strong)]' /> }} />
        </h2>
      </div>
      <div ref={holdRef} className={`${CONTAINER_CLASS} frame-hold mi-embed`} id='mi-hold'>
        <MultiInternetCard />
      </div>
    </section>
  );
}
